import { useState, useCallback, useEffect } from "react";
import { useFetchBoxNumbers } from "./use-fetch-box-numbers";
import { useFetchVehicles } from "./use-fetch-vehicles";
import { useFetchTradeReasons } from "./use-fetch-trade-reasons";
import { useFetchExceptions } from "./use-fetch-exceptions";
import { useFetchLocations } from "./use-fetch-locations";

/**
 * Custom hook to manage all table-related data fetching
 * Extracts data fetching logic from MainTable.js
 *
 * @param {Date} assignmentDate - The assignment date
 * @param {string} vehicleDivision - Vehicle division ID
 * @param {string} pulloutDivision - Pullout division ID
 * @param {boolean} isSpecial - Whether this is special route data
 * @param {Function} setApiErrors - Function to set API errors
 * @param {Function} setErrorMessage - Function to set error messages
 * @returns {Object} All table data and loading state
 */
export const useTableData = ({
  assignmentDate,
  vehicleDivision,
  pulloutDivision,
  isSpecial,
  setApiErrors,
  setErrorMessage,
  onVehicleFetchFailed,
  setVehiclesLoading,
}) => {
  const [vehicles, setVehicles] = useState([]);
  const [duplicateVehicles, setDuplicateVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tradeReasons, setTradeReasons] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [boxNumbers, setBoxNumbers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const specialFlag = isSpecial ? "Y" : "N";
  const fetchBoxNumbers = useFetchBoxNumbers(specialFlag);
  const fetchVehicles = useFetchVehicles();
  const fetchTradeReasons = useFetchTradeReasons();
  const fetchExceptions = useFetchExceptions();
  const fetchLocations = useFetchLocations();

  // Fetch vehicles and duplicate vehicles
  const getVehicles = useCallback(async () => {
    setVehiclesLoading?.(true);
    const response = await fetchVehicles(assignmentDate, vehicleDivision, "N");

    if (!response.success) {
      if (response.code === 501) {
        setApiErrors([response]);
      } else {
        setVehicles([]);
        setDuplicateVehicles([]);
        setErrorMessage(response.message);
        setLoadingData(false);
        onVehicleFetchFailed?.(vehicleDivision);
      }
      setVehiclesLoading?.(false);
      return;
    }

    setVehicles(response.data);

    const duplicateResponse = await fetchVehicles(
      assignmentDate,
      vehicleDivision,
      "Y"
    );

    if (!duplicateResponse.success) {
      if (duplicateResponse.code === 501) {
        setApiErrors([duplicateResponse]);
      } else {
        setDuplicateVehicles([]);
        setErrorMessage(duplicateResponse.message);
        onVehicleFetchFailed?.(vehicleDivision);
      }
      setVehiclesLoading?.(false);
      return;
    }

    setDuplicateVehicles(duplicateResponse.data);
    setVehiclesLoading?.(false);
  }, [
    assignmentDate,
    fetchVehicles,
    vehicleDivision,
    onVehicleFetchFailed,
    setVehiclesLoading,
  ]);

  // Fetch locations
  const getLocationData = useCallback(async () => {
    const response = await fetchLocations(pulloutDivision);
    if (!response.success) {
      if (response.code === 501) {
        setApiErrors([response]);
      } else {
        setErrorMessage(response.message);
        setLoadingData(false);
      }
      return;
    }

    setLocations(response.data);
  }, [fetchLocations, pulloutDivision]);

  // Fetch box numbers
  const getBoxNumbersData = useCallback(async () => {
    setLoadingData(true);
    const response = await fetchBoxNumbers(assignmentDate, pulloutDivision);
    if (!response.success) {
      if (response.code === 501) {
        setApiErrors([response]);
      } else {
        setErrorMessage(response.message);
        setLoadingData(false);
      }
      return;
    }

    setBoxNumbers(response.data);
    setLoadingData(false);
  }, [fetchBoxNumbers, assignmentDate, pulloutDivision]);

  // Fetch exceptions
  const getExceptionsData = useCallback(async () => {
    const response = await fetchExceptions();
    if (!response.success) {
      if (response.code === 501) {
        setApiErrors([response]);
      } else {
        setErrorMessage(response.message);
        setLoadingData(false);
      }
      return;
    }

    setExceptions(response.data);
  }, [fetchExceptions]);

  // Fetch trade reasons
  const getTradeReasonsData = useCallback(async () => {
    const response = await fetchTradeReasons();
    if (!response.success) {
      if (response.code === 501) {
        setApiErrors([response]);
      } else {
        setErrorMessage(response.message);
        setLoadingData(false);
      }
      return;
    }

    setTradeReasons(response.data);
  }, [fetchTradeReasons]);

  // Effects to trigger data fetching
  // Note: Each callback already captures its required dependencies via useCallback,
  // so we only need the callback in the dependency array
  useEffect(() => {
    getVehicles();
  }, [getVehicles]);

  useEffect(() => {
    getLocationData();
  }, [getLocationData]);

  useEffect(() => {
    getBoxNumbersData();
  }, [getBoxNumbersData]);

  useEffect(() => {
    getExceptionsData();
  }, [getExceptionsData]);

  useEffect(() => {
    getTradeReasonsData();
  }, [getTradeReasonsData]);

  return {
    vehicles,
    duplicateVehicles,
    locations,
    tradeReasons,
    exceptions,
    boxNumbers,
    loadingData,
    setVehicles,
    setDuplicateVehicles,
    setLocations,
    setTradeReasons,
    setExceptions,
    setBoxNumbers,
    setLoadingData,
  };
};
